/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { UserRoleRepresentation } from '@veeam-vspc/models/web-controllers';

import type {
    UserCreationSourceRepresentation,
    UserMfaPolicyAggregateStatusRepresentation,
    UserStatusRepresentation,
    IsLoggedUserJson,
    CompanyStatusRepresentation,
} from '@veeam-vspc/models/web-controllers';

export class PortalUser {

    readonly id: number;
    readonly instanceUid: string;
    readonly userRole: UserRoleRepresentation;
    readonly creationSource: UserCreationSourceRepresentation;

    login: string;
    email: string | null;
    firstName: string;
    lastName: string;
    userStatus: UserStatusRepresentation;
    mfaPolicyStatus: UserMfaPolicyAggregateStatusRepresentation;
    requireChangePassword: boolean;
    address: string | null;
    isVcdCompany: boolean;
    companyId: number;
    organizationUid: string;
    companyName: string | null;
    companyStatus: CompanyStatusRepresentation;
    havePasswordLogin: boolean;

    constructor(loginUser: IsLoggedUserJson) {
        const {
            id,
            instanceUid,
            userRole,
            creationSource,
            login,
            email,
            firstName,
            lastName,
            userStatus,
            mfaPolicyStatus,
            requireChangePassword,
            address,
            isVcdCompany,
            companyId,
            organizationUid,
            companyName,
            companyStatus,
            companyUid,
            havePasswordLogin,
        } = loginUser;

        this.id = id;
        this.instanceUid = instanceUid;
        this.userRole = userRole;
        this.creationSource = creationSource;
        this.login = login;
        this.email = email;
        this.firstName = firstName;
        this.lastName = lastName;
        this.userStatus = userStatus;
        this.mfaPolicyStatus = mfaPolicyStatus;
        this.requireChangePassword = requireChangePassword;
        this.address = address;
        this.isVcdCompany = isVcdCompany;
        this.companyId = companyId;
        this.organizationUid = organizationUid;
        this.companyName = companyName;
        this.companyStatus = companyStatus;
        this.organizationUid = companyUid;
        this.havePasswordLogin = havePasswordLogin;
    }

    isPortalAdministrator(): boolean {
        return this.userRole === UserRoleRepresentation.VacAdministrator;
    }

    isLocationAdministrator(): boolean {
        return this.userRole === UserRoleRepresentation.CompanyLocationAdministrator;
    }

    isLocationUser(): boolean {
        return this.userRole === UserRoleRepresentation.CompanyLocationUser;
    }

    isCompanyOwner(): boolean {
        return this.userRole === UserRoleRepresentation.CompanyOwner;
    }

    isCompanyInvoiceAuditor(): boolean {
        return this.userRole === UserRoleRepresentation.Finance;
    }

    isSubtenant(): boolean {
        return this.userRole === UserRoleRepresentation.Subtenant;
    }

    isPortalOperator(): boolean {
        return this.userRole === UserRoleRepresentation.VacOperator;
    }

    isServiceProviderGlobalAdministrator(): boolean {
        return this.userRole === UserRoleRepresentation.ResellerOwner;
    }

    isServiceProviderOperator(): boolean {
        return this.userRole === UserRoleRepresentation.ResellerOperator;
    }

    isServiceProviderUser(): boolean {
        return this.userRole === UserRoleRepresentation.ResellerUser;
    }

    isServiceProviderInvoiceAuditor(): boolean {
        return this.userRole === UserRoleRepresentation.ResellerFinance;
    }

    isServiceProviderAdministrator(): boolean {
        return this.userRole === UserRoleRepresentation.ResellerAdministrator;
    }

    isSiteAdministrator(): boolean {
        return this.userRole === UserRoleRepresentation.CloudConnectAdministrator;
    }

    isCompanyAdministrator(): boolean {
        return this.userRole === UserRoleRepresentation.CompanyAdministrator;
    }

    isPortalReadonlyOperator(): boolean {
        return this.userRole === UserRoleRepresentation.VacReadonlyOperator;
    }

    // composite role checks
    isLocationAdminOrCompanyOwner(): boolean {
        return this.isLocationAdministrator() || this.isCompanyOwner() || this.isCompanyAdministrator();
    }

    isPortalOrSiteAdmin(): boolean {
        return this.isPortalAdministrator()
            || this.isSiteAdministrator()
            || this.isPortalReadonlyOperator();
    }

    // service provider === reseller (historical reasons)
    isServiceProviderGlobalOrUsualAdmin(): boolean {
        return this.isServiceProviderGlobalAdministrator() || this.isServiceProviderAdministrator();
    }

    isResellerPortal(): boolean {
        return this.isServiceProviderGlobalAdministrator()
            || this.isServiceProviderOperator()
            || this.isServiceProviderUser()
            || this.isServiceProviderInvoiceAuditor()
            || this.isServiceProviderAdministrator();
    }

    isCompanyGroup(): boolean {
        return (
            this.isLocationUser()
            || this.isCompanyOwner()
            || this.isCompanyAdministrator()
            || this.isSubtenant()
        );
    }

    isReadOnlyRole(): boolean {
        return (
            this.isLocationUser()
            || this.isServiceProviderUser()
            || this.isPortalReadonlyOperator()
        );
    }

    isAdminPortal() {
        return (
            this.isPortalAdministrator()
            || this.isSiteAdministrator()
            || this.isPortalReadonlyOperator()
            || this.isPortalOperator()
        );
    }

    isClientPortal() {
        return (
            this.isCompanyOwner()
            || this.isCompanyAdministrator()
            || this.isLocationAdministrator()
            || this.isLocationUser()
            || this.isSubtenant()
            || this.isCompanyInvoiceAuditor()
        );
    }

    isAuditor(): boolean {
        return this.isCompanyInvoiceAuditor() || this.isServiceProviderInvoiceAuditor();
    }

    isPassedRoleContained(roles: UserRoleRepresentation[], role: UserRoleRepresentation = this.userRole): boolean {
        return Array.isArray(roles) && roles.length > 0
            ? roles.includes(role)
            : false;
    }

}
